import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Layout from "./layout/Layout";
import Main from "./layout/Main";
import PrivateRoutes from "./layout/PrivateRoutes";
import PublicRoutes from "./layout/PublicRoutes";
import SignUpPage from "./pages/Login/SignUp";
import SignInPage from "./pages/Login/SignIn";
import WhatsappOTPPage from "./pages/Login/WhatsappOTP";
import OnboardingSteps from "./pages/Login/OnboardingSteps";
import QuestDetails from "./pages/Quest/Details/QuestDetails";
import Quest from "./pages/Quest/Quest";
import QuestPerLocation from "./pages/Quest/QuestPerLocation/QuestPerLocation";
import Profile from "./pages/Profile/Profile";
import Inbox from "./pages/Profile/Inbox/Inbox";
import QuestHistory from "./pages/Profile/QuestHistory/QuestHistory";
import Support from "./pages/Profile/Support/Support";
import DataProtectionPolicy from "./pages/Profile/Support/DataProtectionPolicy/DataProtectionPolicy";
import ContactUS from "./pages/Profile/Support/ContactUS/ContactUS";
import Home from "./pages/Home/Home";
import DetailMultipleChoice from "./pages/Quest/DetailMultipleChoice/DetailMultipleChoice";
import QuestCompletionPage from "./pages/Quest/CompleteQuest/CompleteQuest";
import EditUser from "./pages/Profile/EditUser/EditUser";
import WithDraw from "./pages/Profile/WithDraw/WithDraw";
import HistoryWithdraw from "./pages/Profile/HistoryWithdraw/HistoryWithdraw";
import RewardList from "./pages/Profile/Reward/Rewards";
import RewardDetail from "./pages/Profile/Reward/RewardDetail";
import AchievementList from "./pages/Profile/Achievement/Achievements";
import AchievementDetail from "./pages/Profile/Achievement/AchievementDetail";
import QRScan from "./pages/Login/QRScan";
import EarningWithdraw from "./pages/Profile/EarningWithdraw/EarningWithdraw";
import Faq from "./pages/Profile/FAQ/Faq";
import FAQSection from "./pages/Profile/FAQ/FAQSection/FAQSection";
import StoryPost from "./pages/Quest/CompleteQuest/StoryPost";
import Badges from "./pages/Profile/Badges/Badges";
import PowerUp from "./pages/Profile/PowerUps/PowerUps";
import UnderReview from "./pages/UnderReview/UnderReview";
// Placeholder for hashing (replace with actual hashing logic, e.g., crypto)

const pageTitles = {
  "/": "Home",
  "/profile": "Profile",
  "/profile/edit": "Edit Profile",
  "/profile/withdraw": "Withdraw",
  "/profile/history": "Withdrawal History",
  "/profile/rewards": "Rewards",
  "/profile/rewards/detail/:id": "Reward Detail",
  "/profile/achievements": "Achievements",
  "/profile/achievements/detail/:id": "Achievement Detail",
  "/profile/inbox": "Inbox",
  "/profile/quest/history": "Quest History",
  "/profile/support": "Support",
  "/profile/support/protection/policy": "Support Data Protection Policy",
  "/profile/support/contactUS": "Support Contact US",
  "/quest": "Quest",
  "/quest/location": "Quest Per Location",
  "/quest/details/multiple/choice/:id": "Take Quest",
  "/quest/details/completed": "Quest Completion",
  "/register": "Sign Up",
  "/sign-in": "Sign In",
  "/verify-otp": "Verify OTP",
  "/qr-scan": "QR Scan",
  "/preview": "Onboarding Steps",
  "/quest/details/:id": "Quest Details",
  "/profile/power-up": "Power Up",
  "/under-review": "Under Review",
};

const getPageTitle = (pathname, pageTitles) => {
  if (pageTitles[pathname]) {
    return pageTitles[pathname];
  }
  for (const route in pageTitles) {
    const regex = new RegExp(`^${route.replace(/:[^\s/]+/g, "[^/]+")}$`);
    if (regex.test(pathname)) {
      return pageTitles[route];
    }
  }
  return "Unknown Page";
};

const safeGtag = (...args) => {
  if (window.gtag && typeof window.gtag === "function") {
    window.gtag(...args);
  } else {
    console.warn("gtag is not available, skipping:", args);
  }
};

function App() {
  const location = useLocation();
  const [userInfo, setUserInfo] = useState(null);
  const [gtagLoaded, setGtagLoaded] = useState(false);
  const [gtagBlocked, setGtagBlocked] = useState(false);

  const measurementId = process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID;

  // Validate Measurement ID
  useEffect(() => {
    if (!measurementId) {
      console.error(
        "REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID is not defined in .env"
      );
      setGtagBlocked(true);
    }
  }, [measurementId]);

  // Load userInfo from localStorage on mount
  useEffect(() => {
    try {
      const storedUserInfo = localStorage.getItem("userInfo");
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        if (parsedUserInfo?.id && parsedUserInfo?.username) {
          setUserInfo(parsedUserInfo);
        } else {
          localStorage.removeItem("userInfo");
          setUserInfo(null);
        }
      }
    } catch (error) {
      console.error("Error parsing userInfo:", error);
      localStorage.removeItem("userInfo");
      setUserInfo(null);
    }
  }, []);

  // Load gtag.js script
  useEffect(() => {
    if (!measurementId || gtagBlocked) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => setGtagLoaded(true);
    script.onerror = () => {
      console.error(
        "Failed to load gtag.js, possibly blocked by ad blocker or network"
      );
      setGtagBlocked(true);
    };
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [measurementId, gtagBlocked]);

  // Initialize GA4 and set user properties
  useEffect(() => {
    if (!gtagLoaded || gtagBlocked || !measurementId) return;

    const maxAttempts = 20;
    let attempts = 0;

    const checkGtag = setInterval(() => {
      if (window.gtag && typeof window.gtag === "function") {
        clearInterval(checkGtag);

        safeGtag("js", new Date());
        safeGtag("config", measurementId, {
          page_path: location.pathname + location.search,
          page_title: getPageTitle(location.pathname, pageTitles),
        });

        if (userInfo) {
          try {
            const { id, username, phone, city } = userInfo;
            safeGtag("set", "user_properties", {
              custom_user_id: id || "unknown",
              username: username || "unknown",
              hashed_phone: phone || "unknown",
              city: city || "unknown",
            });
            safeGtag("config", measurementId, {
              user_id: id || null,
            });
          } catch (error) {
            console.error("Error setting user properties for GA4:", error);
          }
        } else {
          safeGtag("set", "user_properties", {
            custom_user_id: null,
            username: null,
            hashed_phone: null,
            city: null,
          });
          safeGtag("config", measurementId, { user_id: null });
        }
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          console.warn(`gtag not available after ${maxAttempts} attempts`);
          setGtagBlocked(true);
          clearInterval(checkGtag);
        }
      }
    }, 500);

    return () => clearInterval(checkGtag);
  }, [gtagLoaded, gtagBlocked, userInfo, measurementId, location]);

  // Track page views with city, username, and phone
  useEffect(() => {
    if (!gtagLoaded || gtagBlocked || !measurementId) return;

    const pageTitle = getPageTitle(location.pathname, pageTitles);
    if (userInfo) {
      const { id, username, phone, city } = userInfo;

      safeGtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: pageTitle,
        custom_user_id: id || "anonymous",
        username: username || "unknown",
        hashed_phone: phone || "unknown",
        city: city || "unknown",
      });

      safeGtag("event", "view_frequency", {
        custom_user_id: id,
        value: 1,
      });
    } else {
      safeGtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: pageTitle,
        custom_user_id: "anonymous",
        username: "unknown",
        hashed_phone: "unknown",
        city: "unknown",
      });
    }
  }, [gtagLoaded, gtagBlocked, location, userInfo, measurementId]);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route element={<Main />}>
          <Route element={<PrivateRoutes />}>
            <Route path="/preview" element={<OnboardingSteps />} />
            <Route path="/" element={<Home />} />
            <Route path="/profile" element={<Outlet />}>
              <Route index element={<Profile />} />
              <Route path="edit" element={<EditUser />} />
              <Route path="withdraw" element={<WithDraw />} />
              <Route path="badges" element={<Badges />} />
              <Route path="powerUp" element={<PowerUp />} />
              <Route path="history" element={<HistoryWithdraw />} />
              <Route path="rewards" element={<RewardList />} />
              <Route path="rewards/detail/:id" element={<RewardDetail />} />
              <Route path="achievements" element={<AchievementList />} />
              <Route path="withdraw/success" element={<EarningWithdraw />} />
              <Route
                path="achievements/detail/:id"
                element={<AchievementDetail />}
              />
              <Route path="inbox" element={<Inbox />} />
              <Route path="support" element={<Support />} />
              <Route
                path="support/protection/policy"
                element={<DataProtectionPolicy />}
              />
              <Route path="support/contactUS" element={<ContactUS />} />
              <Route path="quest/history" element={<QuestHistory />} />
              <Route path="support/faq" element={<Faq />} />
              <Route path="support/faq/:section" element={<FAQSection />} />
            </Route>
            <Route path="/quest" element={<Outlet />}>
              <Route index element={<Quest />} />
              <Route
                path="details/multiple/choice/:id"
                element={<DetailMultipleChoice />}
              />
              <Route
                path="details/completed"
                element={<QuestCompletionPage />}
              />
            </Route>
            <Route path="/under-review" element={<UnderReview />} />
          </Route>
          <Route path="/story-post" element={<StoryPost />} />
          <Route element={<PublicRoutes />}>
            <Route path="/register" element={<SignUpPage />} />
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/verify-otp" element={<WhatsappOTPPage />} />
            <Route path="/qr-scan" element={<QRScan />} />
            <Route path="*" element={<Navigate to="/sign-in" />} />
            {/* complete story post */}
          </Route>

          <Route path="/quest/details/:id" element={<QuestDetails />} />
          <Route path="/quest/location/:id" element={<QuestPerLocation />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
